export interface GrowthWheel {
  id: number
  name: string
  version?: number
  completedAssessmentSnapshots?: GrowthWheelCompletedAssessmentSnapshot[]
}

export interface GrowthWheelCompletedAssessmentSnapshot {
  version: number
  capturedAt: string
  userId: number
  evaluations: Array<{
    subCompetencyId: number
    selfLevel: number | null
    leaderLevel: number | null
  }>
}

export interface SubCompetency {
  id: number
  name: string
  description: string
}

export interface CompetencyEvaluation {
  id: number
  selfLevel: number | null
  selfNote: string | null
  leaderLevel: number | null
  leaderNote: string | null
  focus: boolean
}

export interface SubCompetencyWithEvaluation extends SubCompetency {
  competencyName?: string
  competencyId?: number
  levelDescriptions: { [key: number]: string }
  evaluation: CompetencyEvaluation | null
}

export interface Competency {
  id: number
  name: string
  description: string
  subCompetencies?: SubCompetencyWithEvaluation[]
}

export interface GetGrowthWheelByRoleIdDto {
  roleId: number
}

export interface GetGrowthWheelByUserIdDto {
  userId: number
}

export interface UpdateCompetencyEvaluationPayload {
  selfLevel?: number | null
  selfNote?: string | null
  leaderLevel?: number | null
  leaderNote?: string | null
  leaderId?: number | null
  focus?: boolean | null
}

export interface UpdateCompetencyEvaluationDto {
  /** Use 0 when no evaluation row exists yet (lazy create on save). */
  evaluationId: number
  userId: number
  subCompetencyId: number
  payload: UpdateCompetencyEvaluationPayload
}

export interface GrowthWheelAssessmentResponse {
  competencies: Competency[]
  isCompleted: boolean
  completedAt: string | null
}

export interface CompleteGrowthWheelAssessmentDto {
  growthWheelId: number
  userId: number
}

export interface CompleteGrowthWheelAssessmentResponse {
  isCompleted: boolean
  completedAt: string
  snapshot: GrowthWheelCompletedAssessmentSnapshot
}

export interface GrowthWheelSummary {
  id: number
  name: string
  roleId: number
  roleName: string
  competenciesCount: number
  subCompetenciesCount: number
  contributorsCount: number
  hasContent: boolean
}

export interface CreateGrowthWheelForGuildDto {
  roleId: number
}

export interface GrowthWheelEditorStructure {
  growthWheel: {
    id: number
    name: string
    roleId: number
    roleName: string
    version: number
    status: GrowthWheelVersionStatus
  }
  competencies: Array<{
    id: number
    name: string
    description: string
    sortOrder: number
    subCompetencies: Array<{
      id: number
      name: string
      description: string
      sortOrder: number
      competencyId: number
      levelDescriptions: Record<number, string>
    }>
  }>
}

export type GrowthWheelPath = '/growth-wheel' | '/growth-wheel-admin'

export type GrowthWheelVersionStatus = 'draft' | 'active' | 'archived'

export type WheelVersionDisplayStatus = 'draft' | 'active' | 'historical'

export interface GrowthWheelVersionMeta {
  id: number
  version: number
  label: string
  status: GrowthWheelVersionStatus
  createdAt: string
  roleId: number
  roleName: string
  canDelete: boolean
  hasEvaluations: boolean
}

export const toWheelVersionDisplayStatus = (
  status: GrowthWheelVersionStatus,
): WheelVersionDisplayStatus =>
  status === 'archived' ? 'historical' : status
